import { useEffect, useMemo, useRef, useState } from "react";
import swal from "sweetalert";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import { getMenuLanguages, MENU_LANGUAGE_CODES } from "../../../constants/menuLanguages";
import {
  assignProductsToSubCategory,
  createSubCategory,
} from "../../../services/subcategories";
import {
  createProductQuick,
  fetchProducts,
  hiddenChoiceItemsFromKeep,
  syncProductChoicesAndItems,
} from "../../../services/MenuService";
import WizardLivePhone from "../Setup/StartNowWizard/WizardLivePhone";
import {
  buildSubcategoryFormLivePreview,
  productChoiceChildren,
  productTitle,
} from "../Cms/MenuTree/menuTreeUtils";
import ProductChoiceKeepPanel from "../Cms/MenuTree/ProductChoiceKeepPanel";
import { IMAGE_PICKER_ACCEPT, validateImageFile } from "../../../utils/imageValidation";
import "../../../i18n";
import "../Cms/MenuTree/MenuTree.css";
import "./AddCategory.css";

/**
 * Add subcategory: banner (optional) · name in any language (at least one)
 * · orientation · products to attach (optional).
 * On submit: create, then assign products.
 */
const AddSubcategory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const languages = useMemo(() => getMenuLanguages(t), [t]);

  // ------------------------------------------------------------------ names
  const [titles, setTitles] = useState(() =>
    Object.fromEntries(MENU_LANGUAGE_CODES.map((code) => [code, ""]))
  );
  const [openLangs, setOpenLangs] = useState({});
  const setTitle = (code, value) => setTitles((prev) => ({ ...prev, [code]: value }));
  const hasAnyTitle = MENU_LANGUAGE_CODES.some((code) => titles[code]?.trim());
  const isOpen = (code) => Boolean(openLangs[code]) || Boolean(titles[code]?.trim());

  // ------------------------------------------------------------------ banner
  const [file, setFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const fileInputRef = useRef(null);
  useEffect(() => () => imageUrl && URL.revokeObjectURL(imageUrl), [imageUrl]);

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected) return;
    const { isValid, message } = validateImageFile(selected, 5);
    if (!isValid) {
      toast.error(message);
      return;
    }
    setFile(selected);
    setImageUrl(URL.createObjectURL(selected));
  };

  // -------------------------------------------------------------- orientation
  const [displayOrientation, setDisplayOrientation] = useState("vertical");

  // --------------------------------------------------------------- products
  const [allProducts, setAllProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [newProductName, setNewProductName] = useState("");
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  /** { [productId]: choiceId[] } — missing = every choice kept. */
  const [keptChoices, setKeptChoices] = useState({});
  /** { [productId]: { [choiceId]: itemId[] } } — missing = every item kept. */
  const [keptItems, setKeptItems] = useState({});
  const [openProductPanels, setOpenProductPanels] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchProducts();
        if (!cancelled) setAllProducts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("AddSubcategory: products load failed", err);
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const productsById = useMemo(
    () => Object.fromEntries(allProducts.map((p) => [String(p._id), p])),
    [allProducts]
  );
  const livePreview = useMemo(
    () =>
      buildSubcategoryFormLivePreview({
        titles,
        productsById,
        selectedProductIds,
        displayOrientation,
      }),
    [titles, productsById, selectedProductIds, displayOrientation]
  );
  const query = productSearch.trim().toLowerCase();
  const visibleProducts = allProducts.filter(
    (p) => !query || productTitle(p, "").toLowerCase().includes(query)
  );

  const toggleProduct = (id) => {
    const wasSelected = selectedProductIds.includes(id);
    setSelectedProductIds((prev) =>
      wasSelected ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const cancelCreateProduct = () => {
    if (creatingProduct) return;
    setCreatingNew(false);
    setNewProductName("");
  };

  const handleCreateProduct = async () => {
    const name = newProductName.trim();
    if (!name || creatingProduct) return;
    setCreatingProduct(true);
    try {
      const created = await createProductQuick(name);
      if (!created?._id) throw new Error("no id");
      const id = String(created._id);
      setAllProducts((prev) => (prev.some((p) => String(p._id) === id) ? prev : [created, ...prev]));
      setSelectedProductIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setNewProductName("");
      setProductSearch("");
      setCreatingNew(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || t("subFormProductCreateFailed"));
    } finally {
      setCreatingProduct(false);
    }
  };

  // ----------------------------------------------------------------- submit
  const [submitting, setSubmitting] = useState(false);
  const canSubmit = hasAnyTitle && !submitting;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;

    const subCategoryDetails = languages
      .map((l) => ({
        language: l.code,
        title: titles[l.code]?.trim() || "",
        description: "",
      }))
      .filter((d) => d.title);

    const formData = new FormData();
    formData.append("data", JSON.stringify({ subCategoryDetails, displayOrientation }));
    if (file) formData.append("file", file);

    setSubmitting(true);
    let created;
    try {
      created = await createSubCategory(formData);
    } catch (err) {
      console.error("AddSubcategory: create failed", err?.response?.data || err.message);
      toast.error(err?.response?.data?.message || t("subFormCreateFailed"));
      setSubmitting(false);
      return;
    }

    if (selectedProductIds.length && created?._id) {
      try {
        await assignProductsToSubCategory(
          String(created._id),
          selectedProductIds.map((product, index) => ({ product, order: index + 1 }))
        );
        for (const productId of selectedProductIds) {
          const children = productChoiceChildren(productsById[productId]);
          if (!children.length) continue;
          const keptChoiceIds = keptChoices[productId] ?? children.map((c) => c.id);
          await syncProductChoicesAndItems(
            productId,
            keptChoiceIds,
            hiddenChoiceItemsFromKeep(children, keptChoiceIds, keptItems[productId] || {})
          );
        }
      } catch (err) {
        console.error("AddSubcategory: assign products failed", err);
        toast.warn(t("subFormAttachFailed"));
      }
    }

    swal({
      title: t("successTitle"),
      text: t("subcategoryCreated"),
      icon: "success",
      timer: 1000,
      buttons: false,
    });
    setTimeout(() => navigate("/subcategoriesList"), 1000);
  };

  const langInput = (lang, required = false) => (
    <div className="catform__field" key={lang.code}>
      <label className="catform__label" htmlFor={`sub-title-${lang.code}`}>
        <img src={lang.flag} alt="" />
        {lang.label}
        {required && <span className="catform__required">{t("catFormRequired")}</span>}
      </label>
      <input
        id={`sub-title-${lang.code}`}
        type="text"
        className="catform__input"
        value={titles[lang.code]}
        autoFocus
        onChange={(e) => setTitle(lang.code, e.target.value)}
      />
    </div>
  );

  return (
    <>
      <PageTitle activeMenu={t("addNewSubcategory")} motherMenu={t("kitchen")} />

      <div className="catform-with-preview">
      <form className="catform" onSubmit={handleSubmit} noValidate>
        <div className="catform__top">
          <h1 className="catform__title">{t("subFormTitle")}</h1>
          <button
            type="button"
            className="btn catform__link"
            onClick={() => navigate("/subcategoriesList")}
          >
            <i className="fas fa-arrow-left" aria-hidden /> {t("subFormList")}
          </button>
        </div>

        {/* --------------------------------------------------------- banner */}
        <section className="catform__section">
          <h2 className="catform__heading">
            {t("subFormBanner")} <span className="catform__optional">{t("optional")}</span>
          </h2>
          <p className="catform__help">{t("subFormBannerHelp")}</p>
          <input
            ref={fileInputRef}
            hidden
            type="file"
            accept={IMAGE_PICKER_ACCEPT}
            onChange={handleFileChange}
          />
          <div className="catform__photo">
            <button
              type="button"
              className="catform__thumb catform__thumb--banner"
              onClick={() => fileInputRef.current?.click()}
            >
              {imageUrl ? <img src={imageUrl} alt="" /> : <i className="fas fa-image" aria-hidden />}
            </button>
            <div className="catform__photo-actions">
              <button type="button" className="btn catform__small" onClick={() => fileInputRef.current?.click()}>
                {imageUrl ? t("catFormReplacePhoto") : t("subFormChooseBanner")}
              </button>
              {imageUrl && (
                <button
                  type="button"
                  className="btn catform__small is-danger"
                  onClick={() => {
                    setFile(null);
                    setImageUrl(null);
                  }}
                >
                  {t("catFormRemovePhoto")}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------ name */}
        <section className="catform__section">
          <h2 className="catform__heading">{t("catFormStepName")}</h2>
          <p className="catform__pills-label">{t("subFormChooseLanguage")}</p>
          <div className="catform__pills">
            {languages.map((lang) => {
              const filled = Boolean(titles[lang.code]?.trim());
              return (
                <button
                  key={lang.code}
                  type="button"
                  className={`catform__pill ${isOpen(lang.code) ? "is-open" : ""} ${filled ? "is-filled" : ""}`}
                  aria-pressed={isOpen(lang.code)}
                  onClick={() => setOpenLangs((prev) => ({ ...prev, [lang.code]: !prev[lang.code] }))}
                >
                  <img src={lang.flag} alt="" />
                  {lang.label}
                  {filled && <i className="fas fa-check" aria-hidden />}
                </button>
              );
            })}
          </div>
          <div className="catform__lang-fields">
            {languages.filter((lang) => isOpen(lang.code)).map((lang) => langInput(lang))}
          </div>
        </section>

        {/* ----------------------------------------------------- orientation */}
        <section className="catform__section catform__section--orient">
          <div className="catform__row">
            <h2 className="catform__heading">{t("displayOrientation")}</h2>
            <div className="catform__radios">
              {["horizontal", "vertical"].map((value) => (
                <label
                  key={value}
                  className={`catform__radio ${displayOrientation === value ? "is-on" : ""}`}
                >
                  <input
                    type="radio"
                    name="subcategoryDisplayOrientation"
                    value={value}
                    checked={displayOrientation === value}
                    onChange={() => setDisplayOrientation(value)}
                  />
                  {t(value)}
                </label>
              ))}
            </div>
          </div>
        </section>

        {/* -------------------------------------------------------- products */}
        <section className={`catform__section ${productsOpen ? "is-open" : ""}`}>
          <button
            type="button"
            className="catform__fold"
            aria-expanded={productsOpen}
            aria-controls="subform-products"
            onClick={() => setProductsOpen((open) => !open)}
          >
            <h2 className="catform__heading">
              {t("subFormStepProducts")} <span className="catform__optional">{t("optional")}</span>
              {selectedProductIds.length > 0 && (
                <span className="catform__fold-count">{selectedProductIds.length}</span>
              )}
            </h2>
            <i
              className={`fas fa-chevron-down catform__fold-arrow ${productsOpen ? "is-open" : ""}`}
              aria-hidden
            />
          </button>

          <p className="catform__help">{t("subFormProductsHelp")}</p>

          {productsOpen && (
            <div id="subform-products" className="catform__fold-body">
              {selectedProductIds.length > 0 && (
                <div className="catform__chips">
                  {selectedProductIds.map((id) => (
                    <span key={id} className="catform__chip">
                      {productTitle(productsById[id], t("subFormUnknownProduct"))}
                      <button type="button" aria-label={t("remove")} onClick={() => toggleProduct(id)}>
                        <i className="fas fa-times" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <input
                type="search"
                className="catform__input"
                placeholder={t("subFormSearchProducts")}
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />

              <div className="catform__list">
                {productsLoading ? (
                  <div className="catform__empty">{t("loading")}…</div>
                ) : allProducts.length === 0 ? (
                  <div className="catform__empty">{t("subFormNoProducts")}</div>
                ) : visibleProducts.length === 0 ? (
                  <div className="catform__empty">{t("subFormNoMatch")}</div>
                ) : (
                  visibleProducts.map((product) => {
                    const id = String(product._id);
                    const checked = selectedProductIds.includes(id);
                    const choices = productChoiceChildren(product);
                    const hasPanel = checked && choices.length > 0;
                    const panelOpen = hasPanel && (openProductPanels[id] ?? false);
                    return (
                      <div
                        key={id}
                        className={`catform__item ${checked ? "is-checked" : ""} ${
                          panelOpen ? "is-expanded" : ""
                        }`}
                      >
                        <div className="catform__item-head">
                          <label className={`catform__option ${checked ? "is-checked" : ""}`}>
                            <input type="checkbox" checked={checked} onChange={() => toggleProduct(id)} />
                            <span className="catform__option-name">{productTitle(product)}</span>
                            {choices.length > 0 && (
                              <span className={`menu-picker__badge ${hasPanel ? "menu-picker__badge--kept" : ""}`}>
                                {hasPanel
                                  ? t("menuTreeKeptBadge", {
                                      kept: (keptChoices[id] ?? choices.map((c) => c.id)).length,
                                      total: choices.length,
                                    })
                                  : t("menuTreeChoicesBadge", { count: choices.length })}
                              </span>
                            )}
                          </label>
                          {hasPanel && (
                            <button
                              type="button"
                              className={`btn menu-picker__chevron ${panelOpen ? "is-open" : ""}`}
                              onClick={() =>
                                setOpenProductPanels((prev) => ({
                                  ...prev,
                                  [id]: !(prev[id] ?? false),
                                }))
                              }
                              aria-expanded={panelOpen}
                            >
                              <i className="fas fa-chevron-down" aria-hidden />
                            </button>
                          )}
                        </div>
                        {panelOpen && (
                          <div className="menu-picker__panel">
                            <p className="menu-picker__subtitle mb-2">{t("prodFormKeepChoicesHint")}</p>
                            <ProductChoiceKeepPanel
                              productId={id}
                              choices={choices}
                              keptChoiceIds={keptChoices[id]}
                              onKeptChoicesChange={(ids) =>
                                setKeptChoices((prev) => ({ ...prev, [id]: ids }))
                              }
                              keptItems={keptItems[id] || {}}
                              onKeptItemsChange={(choiceId, ids) =>
                                setKeptItems((prev) => ({
                                  ...prev,
                                  [id]: { ...(prev[id] || {}), [choiceId]: ids },
                                }))
                              }
                              t={t}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="catform__new">
                {creatingNew ? (
                  <div className="menu-picker__create-form">
                    <input
                      type="text"
                      className="menu-picker__create-input"
                      placeholder={t("subFormNewProductPlaceholder")}
                      aria-label={t("subFormNewProductPlaceholder")}
                      value={newProductName}
                      disabled={creatingProduct}
                      autoFocus
                      onChange={(e) => setNewProductName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCreateProduct();
                        }
                        if (e.key === "Escape") cancelCreateProduct();
                      }}
                    />
                    <button
                      type="button"
                      className="btn menu-picker__create-submit"
                      disabled={!newProductName.trim() || creatingProduct}
                      onClick={handleCreateProduct}
                    >
                      {creatingProduct ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                      ) : (
                        t("menuTreeCreate")
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn menu-picker__create-cancel"
                      onClick={cancelCreateProduct}
                      disabled={creatingProduct}
                      aria-label={t("cancel")}
                    >
                      <i className="fas fa-times" aria-hidden />
                    </button>
                  </div>
                ) : (
                  <button type="button" className="btn menu-picker__create" onClick={() => setCreatingNew(true)}>
                    <span className="menu-picker__create-plus" aria-hidden>
                      <i className="fas fa-plus" />
                    </span>
                    {t("menuTreeNewProduct")}
                  </button>
                )}
              </div>
            </div>
          )}
        </section>

        {/* --------------------------------------------------------- actions */}
        <div className="catform__actions">
          <button
            type="button"
            className="btn catform__btn catform__btn--cancel"
            onClick={() => navigate("/subcategoriesList")}
            disabled={submitting}
          >
            {t("cancel")}
          </button>
          <button type="submit" className="btn catform__btn catform__btn--primary" disabled={!canSubmit}>
            {submitting ? t("subFormCreating") : t("subFormCreate")}
          </button>
        </div>
      </form>

      <aside className="menu-manage-live" aria-label={t("wizardPreviewLive")}>
        <WizardLivePhone
          draft={livePreview.draft}
          productPhotos={livePreview.productPhotos}
          compact
          showStepLabel={false}
          emptyHintKey="subFormLiveEmpty"
        />
      </aside>
      </div>
    </>
  );
};

export default AddSubcategory;
